import { toast as sonnerToast } from 'sonner';

/**
 * Global "session teardown" gate for toasts.
 *
 * When the server invalidates the session mid-use (user disabled/deleted, or
 * logged out elsewhere), every component that happens to be mounting fires its
 * own API calls. Those all come back 401/403 at once and each call site's
 * `catch` pops a contextual toast ("Failed to fetch roles", "Failed to load
 * sections", …). There is no single place those go through, so instead of
 * editing ~50 call sites we gate the two toast systems the app uses:
 *
 *   - sonner        -> monkey-patched here (installToastGuard)
 *   - shadcn/useToast -> gated inside src/hooks/use-toast.ts via isSessionTearingDown()
 *
 * While a teardown is in progress every toast is swallowed, and we surface a
 * single clean "your session ended" notice instead (showSessionEndedNotice,
 * which bypasses the gate via the saved originals).
 */

let tearingDown = false;
let resetTimer: ReturnType<typeof setTimeout> | null = null;

export const isSessionTearingDown = (): boolean => tearingDown;


export const beginSessionTeardown = (): void => {
  tearingDown = true;
  if (resetTimer) clearTimeout(resetTimer);
  resetTimer = setTimeout(() => {
    tearingDown = false;
    resetTimer = null;
  }, 5000);
};

/** Leave teardown early (e.g. a successful response / re-login proves we're live). */
export const endSessionTeardown = (): void => {
  tearingDown = false;
  if (resetTimer) {
    clearTimeout(resetTimer);
    resetTimer = null;
  }
};

type ToastFn = (...args: any[]) => any;
const SONNER_METHODS = ['error', 'success', 'warning', 'info', 'message', 'loading'] as const;
const originals: Partial<Record<(typeof SONNER_METHODS)[number], ToastFn>> = {};
let installed = false;


export const installToastGuard = (): void => {
  if (installed) return;
  installed = true;
  SONNER_METHODS.forEach((m) => {
    const fn = (sonnerToast as any)[m];
    if (typeof fn !== 'function') return;
    const orig = fn.bind(sonnerToast) as ToastFn;
    originals[m] = orig;
    (sonnerToast as any)[m] = (...args: any[]) => (tearingDown ? undefined : orig(...args));
  });
};


export const showSessionEndedNotice = (): void => {
  const msg = 'Your session has ended. Your account may have been deactivated. Please sign in again.';
  const opts = { id: 'session-ended' };
  const orig = originals.error;
  if (orig) orig(msg, opts);
  else sonnerToast.error(msg, opts);
};
